import { css } from '@inter-ikea-kompis/web-component';
import type { ITheme } from '@inter-ikea-kompis/types/lib/index.js';

import StatusIndicatorColorEnum from '../../enums/StatusIndicatorColorEnum.js';
import StatusIndicatorDotPositionEnum from '../../enums/StatusIndicatorDotPositionEnum.js';

type StyleOptions = {
	theme: ITheme;
	statusDotPosition: StatusIndicatorDotPositionEnum;
	statusColor: StatusIndicatorColorEnum | null;
	small: boolean;
};

export default class StatusIndicatorStyle {
	/**
	 * Returns styles.
	 *
	 * @param options Options.
	 * @param options.theme Theme.
	 * @returns Styles.
	 */
	public static getStyle(options: StyleOptions): string {
		const { theme, statusDotPosition, small } = options;
		const { color, spacing, border } = theme;
		const size = (small ? spacing.space50 : spacing.space75) / 16;
		return css`
			:host {
				display: flex;
				align-items: center;
			}
			.container {
				display: inline-flex;
				flex-direction: ${statusDotPosition === StatusIndicatorDotPositionEnum.leading
					? 'row'
					: 'row-reverse'};
				min-height: ${22 / 16}rem;
			}
			.divider {
				width: ${size}rem;
				flex-shrink: 0;
			}
			.dot {
				width: ${size}rem;
				height: ${size}rem;
				border-radius: ${border.radius.radius64 / 16}rem;
				print-color-adjust: exact;
				-webkit-print-color-adjust: exact;
				background-color: ${this.getDotBackgroundColor(options)};
				border-color: ${this.getStatusIndicatorColor(options)};
				flex-shrink: 0;
				position: relative;
				border-style: solid;
				border-width: ${2 / 16}rem;
				margin-top: ${5 / 16}rem;
			}
			kompis-text {
				color: ${color.neutral.grey700};
			}
			${this.getDotFilledStyle(options)}
		`;
	}

	/**
	 * Returns background color depending on size and color.
	 *
	 * @param options Options.
	 * @returns Color.
	 */
	private static getDotBackgroundColor(options: StyleOptions): string {
		const { small, statusColor } = options;
		if (small) {
			return this.getStatusIndicatorColor(options);
		}
		if (statusColor === StatusIndicatorColorEnum.green) {
			return options.theme.color.semantic.positive;
		}
		return 'transparent';
	}

	/**
	 * Returns the correct fill for the dot depending on color.
	 *
	 * @param options Options.
	 * @returns Styles.
	 */
	private static getDotFilledStyle(options: StyleOptions): string {
		const { statusColor, theme } = options;
		switch (statusColor) {
			case StatusIndicatorColorEnum.orange:
				return css`
					.dotFill {
						position: absolute;
						top: 50%;
						bottom: 0;
						left: 0;
						right: 0;
						background-color: ${theme.color.semantic.caution};
					}
				`;
			case StatusIndicatorColorEnum.grey:
				return css`
					.dotFill {
						position: absolute;
						top: 0;
						bottom: 0;
						left: 50%;
						box-shadow: 0 0 0 ${1 / 16}rem ${theme.color.neutral.grey500};
						transform: rotate(-45deg);
					}
				`;
			default:
				return '';
		}
	}

	/**
	 * Retrives Status Color.
	 *
	 * @param options ...
	 * @returns Color string.
	 */
	private static getStatusIndicatorColor(options: StyleOptions): string {
		const { color } = options.theme;
		switch (options.statusColor) {
			case StatusIndicatorColorEnum.green:
				return color.semantic.positive;
			case StatusIndicatorColorEnum.orange:
				return color.semantic.caution;
			case StatusIndicatorColorEnum.grey:
				return color.neutral.grey500;
			case StatusIndicatorColorEnum.red:
				return color.semantic.negative;
			default:
				return '';
		}
	}
}
